import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { TextFieldModule } from '@angular/cdk/text-field';
import { CommonModule } from '@angular/common';
import { Component, computed, EventEmitter, inject, Input, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LangChangeEvent, TranslateModule, TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { firstValueFrom, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AuthService } from 'src/app/auth.service';
import { VoiceRecorderButtonComponent } from 'src/app/shared/components/recorders/voice-recorder-button/voice-recorder-button.component';
import { VoiceNoteCardComponent } from 'src/app/shared/components/ui/voice-note-card/voice-note-card.component';
import { ConfirmMenuService } from 'src/app/shared/services/confirm-menu/confirm-menu.service';
import { ErrorHandlerService } from 'src/app/shared/services/error-handler.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { isFileAllowed } from 'src/app/shared/utils/file-validation.utils';
import { StorageService } from 'src/app/storage.service';
import { SelectComponent, SelectOption } from 'src/app/shared/components/ui/select/select.component';
import { CheckboxComponent } from 'src/app/shared/components/ui/checkbox/checkbox.component';
import { Chunk } from '../chunk/chunk.dto';
import { ChunkService } from '../chunk/chunk.service';
import { ContentSearcherComponent, ContentSearchResult } from '../content-searcher/content-searcher.component';
import { ContentStateService } from '../content-state.service';
import { ConversationService, QuizContent, QuizData } from '../conversation.service';
import { QuizComponent } from '../generate-content-pane/components/quiz/quiz.component';

@Component({
  selector: 'app-quiz-generator',
  templateUrl: './quiz-generator.component.html',
  styleUrls: ['./quiz-generator.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatRadioModule,
    MatSelectModule,
    MatTooltipModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    TranslateModule,
    QuizComponent,
    TextFieldModule,
    VoiceRecorderButtonComponent,
    VoiceNoteCardComponent,
    SelectComponent,
    CheckboxComponent,
  ],
})
export class QuizGeneratorComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private storageService = inject(StorageService);
  private contentStateService = inject(ContentStateService);
  private conversationService = inject(ConversationService);
  private chunkService = inject(ChunkService);
  private translate = inject(TranslateService);
  private authService = inject(AuthService);
  private toast = inject(ToastService);
  private confirmMenuService = inject(ConfirmMenuService);
  private errorHandler = inject(ErrorHandlerService);

  @Input() contentID: string;
  @Output() close = new EventEmitter<void>();

  quizForm: FormGroup;
  private destroy$ = new Subject<void>();
  private cacheKey: string;
  private lastLang: string;

  isPreviewMode = signal(false);
  previewData: any = null;
  isSubmitting = false;
  isTranslating = signal(false);
  isSaving = signal(false);
  showSavedStatus = signal(false);
  canEditExistingQuizzes = signal(false); // we disable editing existing quizzes for now
  isLoadingDrafts = signal(true);
  private savedTimeout: any;

  viewMode = signal<'LIST' | 'MANUAL' | 'AI_SETUP' | 'AI_LOADING'>('LIST');
  aiSetupForm: FormGroup;
  quizDraftId = signal<string | null>(null);
  drafts = signal<any[]>([]);
  fullDraftContent: QuizContent = { en: null, fr: null };
  attachedExternalContexts = signal<ContentSearchResult[]>([]);
  attachedFiles = signal<{ id: string; file?: File; url?: string; filename?: string; type: 'audio' | 'file' | 'video' | 'image'; name: string; uploading?: boolean }[]>([]);
  isRecordingVoice = signal(false);

  hasDraft = computed(() => this.drafts().length > 0);

  existingQuizzes = computed(() => {
    return this.contentStateService.content().filter((c) => c.type === 'quiz');
  });

  difficultyOptions = [
    { value: 'easy', label: 'Quiz.DifficultyEasy' },
    { value: 'medium', label: 'Quiz.DifficultyMedium' },
    { value: 'hard', label: 'Quiz.DifficultyHard' },
  ];

  quizTypeOptions: SelectOption[] = [
    { value: 'multiple-choice', label: 'Quiz.TypeMultipleChoice' },
    { value: 'free-text', label: 'Quiz.TypeFreeText' },
    { value: 'mix', label: 'Quiz.TypeMix' },
  ];

  cannotUseAI = computed(() => !this.authService.canUseAI());

  constructor() {
    this.quizForm = this.fb.group({
      title: ['', Validators.required],
      questions: this.fb.array([], Validators.required),
    });

    this.aiSetupForm = this.fb.group({
      subject: ['', Validators.required],
      count: [5, [Validators.required, Validators.min(1), Validators.max(15)]],
      difficulty: ['medium', Validators.required],
      context: [''],
      quizType: ['multiple-choice', Validators.required],
    });
  }

  get questions(): FormArray {
    return this.quizForm.get('questions') as FormArray;
  }

  ngOnInit(): void {
    if (!this.contentID) {
      console.error('ContentID is required for QuizGeneratorComponent');
      return;
    }
    this.loadDrafts();

    this.lastLang = this.translate.currentLang;
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(async (event: LangChangeEvent) => {
      if (this.viewMode() === 'MANUAL' && this.quizDraftId()) {
        this.isTranslating.set(true);
        const formVal = this.quizForm.getRawValue();

        // 1. Save current language changes to DB (using the OLD language)
        try {
          if (this.quizForm.dirty) {
            await firstValueFrom(this.saveDraftToDb(formVal, this.lastLang));
          }

          // 2. Fetch fresh data from DB to get latest updates for the NEW language
          const freshDraft = await firstValueFrom(this.conversationService.getQuizDraftById(this.quizDraftId()!));

          if (freshDraft && freshDraft.quizContent) {
            // Handle legacy/bilingual structure normalization
            if (freshDraft.quizContent.en || freshDraft.quizContent.fr) {
              this.fullDraftContent = freshDraft.quizContent;
            } else {
              this.fullDraftContent = { en: freshDraft.quizContent, fr: freshDraft.quizContent };
            }
          }
        } catch (e) {
          console.error('Failed to sync language switch', e);
        } finally {
          this.isTranslating.set(false);
        }
      }

      this.lastLang = event.lang;
      this.updateFormForLanguage(event.lang);
    });

    this.quizForm.valueChanges
      .pipe(
        filter(() => this.quizForm.dirty),
        debounceTime(2000),
        switchMap((val) =>
          this.saveDraftToDb(val).pipe(
            catchError((err) => {
              console.error('Autosave error', err);
              return of(null);
            })
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.aiSetupForm.valueChanges.pipe(
      debounceTime(1000),
      takeUntil(this.destroy$)
    ).subscribe((val) => {
      if (this.quizDraftId()) {
        this.conversationService.updateQuizDraft(this.quizDraftId()!, {
          subject: val.subject,
          count: val.count,
          difficulty: val.difficulty,
          context: val.context,
          quizType: val.quizType
        }).subscribe({
          next: (updatedDraft) => this.updateLocalDraft(updatedDraft),
          error: (err) => console.error('Failed to auto-save setup draft', err)
        });
      }
    });
  }

  updateFormForLanguage(lang: string) {
    if (!this.fullDraftContent) return;

    const data = this.fullDraftContent[lang];
    if (data) {
      this.quizForm.patchValue({ title: data.title }, { emitEvent: false });

      const newQuestions = new FormArray([]);
      if (data.questions && Array.isArray(data.questions)) {
        data.questions.forEach((q: any) => {
          newQuestions.push(this.createQuestion(q));
        });
      } else {
        newQuestions.push(this.createQuestion());
      }
      this.quizForm.setControl('questions', newQuestions, { emitEvent: false });
    } else {
      // Clear form if no data for this language
      this.quizForm.patchValue({ title: '' }, { emitEvent: false });
      const newQuestions = new FormArray([]);
      newQuestions.push(this.createQuestion());
      this.quizForm.setControl('questions', newQuestions, { emitEvent: false });
    }
    this.quizForm.markAsPristine();
  }

  resumeDraft(draftId: string) {
    this.viewMode.set('AI_LOADING');
    this.conversationService.getQuizDraftById(draftId).subscribe((draft) => {
      if (draft && draft.quizContent) {
        this.quizDraftId.set(draft._id);

        // Handle both legacy flat structure and new bilingual structure
        if (draft.quizContent.en || draft.quizContent.fr) {
          this.fullDraftContent = draft.quizContent;
        } else {
          // Legacy flat structure: mirror it to both to be safe
          this.fullDraftContent = { en: draft.quizContent, fr: draft.quizContent };
        }

        const currentLang = this.translate.currentLang;
        const data = this.fullDraftContent[currentLang] || this.fullDraftContent['en'] || this.fullDraftContent['fr'];

        if (data) {
          this.quizForm.patchValue({ title: data.title }, { emitEvent: false });

          const newQuestions = new FormArray([]);
          if (data.questions && Array.isArray(data.questions)) {
            data.questions.forEach((q: any) => {
              newQuestions.push(this.createQuestion(q));
            });
          } else {
            // Ensure at least one empty question if none exist
            newQuestions.push(this.createQuestion());
          }
          this.quizForm.setControl('questions', newQuestions, { emitEvent: false });
        }
        this.viewMode.set('MANUAL');
      } else {
        this.viewMode.set('LIST'); // Fallback if failed
      }
    });
  }

  loadDrafts() {
    this.isLoadingDrafts.set(true);
    this.conversationService.getQuizDrafts(this.contentID).subscribe((drafts) => {
      this.drafts.set(drafts || []);
      this.isLoadingDrafts.set(false);
    });
  }

  resumeSetupDraft(draft: any) {
    this.quizDraftId.set(draft._id);
    this.aiSetupForm.patchValue({
      subject: draft.subject || '',
      count: draft.count || 5,
      difficulty: draft.difficulty || 'medium',
      context: draft.context || '',
      quizType: draft.quizType || 'multiple-choice'
    }, { emitEvent: false });
    
    if (draft.mediaContent && Array.isArray(draft.mediaContent) && draft.mediaContent.length > 0) {
      this.attachedFiles.set(draft.mediaContent.map((m: any, i: number) => ({
        id: `draft_media_${i}`,
        url: m.url,
        type: m.type,
        filename: m.filename,
        name: m.filename ? m.filename.split('/').pop()?.split('_').slice(1).join('_') || 'Attached File' : 'Attached File',
        uploading: false
      })));
    } else {
      this.attachedFiles.set([]);
    }
    
    this.viewMode.set('AI_SETUP');
  }

  private updateLocalDraft(updatedDraft: any) {
    if (!updatedDraft || !updatedDraft._id) return;
    this.drafts.update(drafts => drafts.map(d => d._id === updatedDraft._id ? updatedDraft : d));
  }

  saveDraftToDb(data: any, language?: string): Observable<any> {
    const quizContent = this.transformToQuizContent(data);
    const currentLang = language || this.translate.currentLang;

    // Update the specific language in our local full state
    if (!this.fullDraftContent) {
      this.fullDraftContent = { en: null, fr: null };
    }
    this.fullDraftContent[currentLang] = quizContent;

    // If the other language is missing (e.g. new manual creation), mirror it initially so it's not null
    const otherLang = currentLang === 'en' ? 'fr' : 'en';
    if (!this.fullDraftContent[otherLang]) {
      this.fullDraftContent[otherLang] = quizContent;
    }

    this.isSaving.set(true);
    this.showSavedStatus.set(false);
    if (this.savedTimeout) clearTimeout(this.savedTimeout);

    const saveObs = this.quizDraftId()
      ? this.conversationService.updateQuizDraft(this.quizDraftId()!, { quizContent: this.fullDraftContent, modifiedLang: currentLang })
      : this.conversationService.createQuizDraft(this.contentID, this.fullDraftContent);

    return saveObs.pipe(
      tap((res) => {
        if (res && res._id) {
          this.quizDraftId.set(res._id);
        }
        this.handleSaveComplete();
      }),
      catchError((err) => {
        this.isSaving.set(false);
        throw err;
      })
    );
  }

  private handleSaveComplete() {
    this.isSaving.set(false);
    this.showSavedStatus.set(true);
    if (this.savedTimeout) clearTimeout(this.savedTimeout);
    this.savedTimeout = setTimeout(() => {
      this.showSavedStatus.set(false);
    }, 4000);
  }

  // --- File Attachment Methods ---

  async handleFiles(files: FileList) {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!isFileAllowed(file)) {
        this.toast.snackbar.open(this.translate.instant('FileUploader.UnsupportedFileType') || 'File type not supported', 'Close', { duration: 3000 });
        continue;
      }
      let mediaType: 'audio' | 'file' | 'video' | 'image' = 'file';
      if (file.type.startsWith('audio/')) mediaType = 'audio';
      else if (file.type.startsWith('video/')) mediaType = 'video';
      else if (file.type.startsWith('image/')) mediaType = 'image';

      const fileId = Date.now().toString() + '_' + i;
      this.attachedFiles.update((filesArray) => [...filesArray, { id: fileId, file, name: file.name, type: mediaType, uploading: true }]);
      this.uploadFileInternal(file, fileId);
    }
  }

  async onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files || files.length === 0) return;
    this.handleFiles(files);
    input.value = '';
  }

  private async uploadFileInternal(file: File, fileId: string) {
    try {
      const filename = `quiz_generator_attachments/${this.contentStateService.contentId}/${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
      const snapshot: any = await this.authService.uploadFile(file, filename, undefined, true);
      const gsUrl = `gs://${snapshot.ref.bucket}/${snapshot.ref.fullPath}`;
      this.attachedFiles.update((filesArray) => filesArray.map((f) => (f.id === fileId ? { ...f, url: gsUrl, filename, uploading: false } : f)));
      this.saveMediaContexts();
    } catch (err) {
      console.error('File upload failed', err);
      this.toast.snackbar.open(this.translate.instant('Snackbar.Error') || 'Upload failed', 'Close', { duration: 3000 });
      this.removeAttachment(fileId);
    }
  }

  removeAttachment(fileId: string) {
    const fileToRemove = this.attachedFiles().find((f) => f.id === fileId);
    if (fileToRemove && fileToRemove.filename) {
      this.authService.deleteFile(fileToRemove.filename).catch(err => console.error('Failed to delete file from storage', err));
    }
    this.attachedFiles.update((filesArray) => filesArray.filter((f) => f.id !== fileId));
    this.saveMediaContexts();
  }

  saveMediaContexts() {
    if (!this.quizDraftId()) return;
    
    const readyAttachments = this.attachedFiles().filter((a) => !a.uploading && a.url);
    const mediaContent = readyAttachments.map((a) => ({ url: a.url!, type: a.type, filename: a.filename }));
    const attachedContentIds = this.attachedExternalContexts().map((c) => c.id);
    
    this.conversationService.updateQuizDraft(this.quizDraftId()!, {
      mediaContent,
      attachedContentIds
    }).subscribe({
      next: (updatedDraft) => this.updateLocalDraft(updatedDraft),
      error: (err) => console.error('Failed to auto-save media context', err)
    });
  }

  allFilesDoneUploading() {
    return this.attachedFiles().every((a) => !a.uploading);
  }

  onVoiceRecorded(file: File) {
    const id = Date.now().toString();
    this.attachedFiles.update((files) => [...files, { id, file, name: file.name, type: 'audio', uploading: true }]);
    this.uploadFileInternal(file, id);
  }

  ngOnDestroy(): void {
    // Setup drafts are now fully persistent in the database.
    // We intentionally do NOT delete attachments here on component destruction.
    // Files are only deleted when:
    // 1. Manually removed via removeAttachment
    // 2. Setup is explicitly cancelled via cancelAiSetup
    // 3. Quiz generation succeeds and consumes the context via generateAiQuiz
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- Form Helpers ---

  createQuestion(data?: any): FormGroup {
    const questionType = data?.type || 'multiple-choice';
    let options = data?.options;

    // Handle legacy string[] options from DB
    if (options && Array.isArray(options) && options.length > 0 && typeof options[0] === 'string') {
      options = options.map((opt: string, index: number) => {
        const isCorrect = data.correctAnswerIndices && Array.isArray(data.correctAnswerIndices)
          ? data.correctAnswerIndices.includes(index)
          : index === (data.correctAnswerIndex ?? -1);
        return {
          label: opt,
          isCorrect,
        };
      });
    }

    // Default options
    if (!options || !Array.isArray(options) || options.length === 0) {
      options = [
        { label: '', isCorrect: true },
        { label: '', isCorrect: false },
      ];
    }

    const group = this.fb.group({
      type: [questionType, Validators.required],
      text: [data?.text || '', Validators.required],
      options: this.fb.array(options.map((opt: any) => this.createOption(opt, questionType))),
      explanation: [data?.explanation || ''],
    });

    // Listen to type changes
    group.get('type').valueChanges.subscribe((type) => {
      const optsArray = group.get('options') as FormArray;
      if (type === 'free-text') {
        optsArray.controls.forEach((ctrl) => {
          ctrl.get('label').clearValidators();
          ctrl.get('label').updateValueAndValidity({ emitEvent: false });
        });
      } else {
        optsArray.controls.forEach((ctrl) => {
          ctrl.get('label').setValidators(Validators.required);
          ctrl.get('label').updateValueAndValidity({ emitEvent: false });
        });
      }
      group.updateValueAndValidity();
    });

    return group;
  }

  createOption(data?: any, questionType = 'multiple-choice'): FormGroup {
    return this.fb.group({
      label: [data?.label || '', questionType === 'free-text' ? [] : Validators.required],
      isCorrect: [data?.isCorrect || false],
    });
  }

  addQuestion() {
    this.questions.push(this.createQuestion());
    this.quizForm.markAsDirty();
    this.quizForm.updateValueAndValidity();
  }

  removeQuestion(index: number) {
    this.questions.removeAt(index);
    this.quizForm.markAsDirty();
    this.quizForm.updateValueAndValidity();
  }

  deleteQuizChunk(chunk: Chunk, trigger: MatIconButton) {
    this.confirmMenuService.confirm(trigger._elementRef, 'Global.AreYouSure', 'delete_outline').subscribe(() => {
      this.chunkService
        .deleteChunk({
          chunkID: chunk._id,
          contentID: this.contentID,
          type: 'quiz',
        })
        .subscribe(() => {
          this.contentStateService.getPieces(true, true);
        });
    });
  }

  startNewManual() {
    this.quizDraftId.set(null);
    this.fullDraftContent = { en: null, fr: null };
    this.quizForm.reset();
    this.questions.clear();
    this.addQuestion();
    this.viewMode.set('MANUAL');
  }

  getOptions(questionIndex: number): FormArray {
    return this.questions.at(questionIndex).get('options') as FormArray;
  }

  addOption(questionIndex: number) {
    this.getOptions(questionIndex).push(this.createOption());
    this.quizForm.markAsDirty();
    this.quizForm.updateValueAndValidity();
  }

  removeOption(questionIndex: number, optionIndex: number) {
    this.getOptions(questionIndex).removeAt(optionIndex);
    this.quizForm.markAsDirty();
    this.quizForm.updateValueAndValidity();
  }

  setCorrectOption(questionIndex: number, optionIndex: number) {
    const options = this.getOptions(questionIndex);
    const ctrl = options.at(optionIndex).get('isCorrect');
    ctrl.setValue(!ctrl.value);
    this.quizForm.markAsDirty();
  }

  // --- Actions ---

  togglePreview() {
    if (this.quizForm.invalid) {
      this.quizForm.markAllAsTouched();
      return;
    }

    if (!this.isPreviewMode()) {
      const formVal = this.quizForm.getRawValue();
      this.previewData = this.transformToQuizContent(formVal);
    }
    this.isPreviewMode.set(!this.isPreviewMode());
  }

  transformToQuizContent(formValue: any): QuizData {
    return {
      title: formValue.title,
      questions: formValue.questions.map((q: any, i: number) => {
        if (q.type === 'free-text') {
          return {
            type: 'free-text',
            text: q.text,
            explanation: q.explanation,
          };
        }
        const correctIndices = q.options
          .map((o: any, idx: number) => (o.isCorrect ? idx : null))
          .filter((idx: any) => idx !== null);

        return {
          type: 'multiple-choice',
          text: q.text,
          options: q.options.map((o: any) => o.label || ''),
          correctAnswerIndex: correctIndices[0] ?? -1,
          correctAnswerIndices: correctIndices,
          explanation: q.explanation,
        };
      }),
    };
  }

  async translateDraft() {
    if (!this.quizDraftId()) return;

    this.isTranslating.set(true);
    try {
      // Ensure current changes are saved first
      const formVal = this.quizForm.getRawValue();
      await firstValueFrom(this.saveDraftToDb(formVal));

      const currentLang = this.translate.currentLang;
      this.conversationService.translateQuizDraft(this.quizDraftId()!, currentLang).subscribe({
        next: (updatedContent) => {
          this.fullDraftContent = updatedContent;
          this.isTranslating.set(false);
          this.toast.snackbar.open(this.translate.instant('Global.Saved'), 'Ok', { duration: 2000 });
        },
        error: (err) => {
          console.error(err);
          this.errorHandler.handleAiRestrictedError(err);
          this.isTranslating.set(false);
        },
      });
    } catch (e) {
      console.error(e);
      this.isTranslating.set(false);
    }
  }

  async insertQuiz() {
    if (this.quizForm.invalid) {
      this.quizForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    const formVal = this.quizForm.getRawValue();

    // Ensure draft is up to date
    await firstValueFrom(this.saveDraftToDb(formVal));

    const quizContent = this.transformToQuizContent(formVal);

    try {
      const userData = this.authService.userInfo();
      // Safe fallback if signal is empty, though unlikely if logged in
      const safeUser = userData || JSON.parse(localStorage.getItem('userData'));

      const newChunkId = (moment().unix() * 1000).toString();

      const chunkPayload = {
        _id: newChunkId,
        content: quizContent,
        type: 'quiz',
        language: this.translate.currentLang,
        status: 'creating',
        params: {},
        editions: [],
        user: {
          id: safeUser?.id,
          fullName: `${safeUser?.firstName} ${safeUser?.lastName}`,
          firstName: safeUser?.firstName,
          lastName: safeUser?.lastName,
          email: safeUser?.email,
        },
      };

      const payload = {
        chunks: [
          {
            ...chunkPayload,
            courseID: this.contentStateService.courseId,
            lecture: this.contentStateService.lecture,
            section: this.contentStateService.section,
            blogID: this.contentStateService.blogId,
            noteID: this.contentStateService.noteId,
            contentID: this.contentID,
            contentName: formVal.title,
          },
        ],
        insertBelowID: undefined,
      };

      this.chunkService.createChunk(payload).subscribe({
        next: () => {
          this.isSubmitting = false;
          this.toast.snackbar.open(this.translate.instant('Global.Saved'), 'Ok', { duration: 2000 });
          this.close.emit();
        },
        error: (err) => {
          console.error(err);
          this.isSubmitting = false;
        },
      });
    } catch (e) {
      console.error(e);
      this.isSubmitting = false;
    }
  }

  async saveQuiz() {
    if (this.quizForm.invalid) {
      this.quizForm.markAllAsTouched();
      return;
    }

    try {
      this.isSubmitting = true;
      const formVal = this.quizForm.getRawValue();

      // Save Draft (force update backend)
      await firstValueFrom(this.saveDraftToDb(formVal));
      this.isSubmitting = false;
      this.toast.snackbar.open(this.translate.instant('Global.Saved'), 'Ok', { duration: 2000 });
    } catch (e) {
      console.error(e);
      this.isSubmitting = false;
    }
  }

  // --- AI Workflow ---

  goBackToList() {
    this.viewMode.set('LIST');
  }

  openAiSetup() {
    if (this.cannotUseAI()) return;
    const existingSetupDraft = this.drafts().find((d) => d.status === 'setup');
    if (existingSetupDraft) {
      this.resumeSetupDraft(existingSetupDraft);
      return;
    }

    this.viewMode.set('AI_SETUP');
    if (!this.quizDraftId()) {
      this.conversationService.createQuizDraft(this.contentID, null, {
        status: 'setup',
        subject: this.aiSetupForm.value.subject || '',
        count: this.aiSetupForm.value.count || 5,
        difficulty: this.aiSetupForm.value.difficulty || 'medium',
        context: this.aiSetupForm.value.context || '',
        quizType: this.aiSetupForm.value.quizType || 'multiple-choice'
      }).subscribe({
        next: (draft) => {
          if (draft._id) {
            this.quizDraftId.set(draft._id);
            this.drafts.update(drafts => [draft, ...drafts]);
          }
        },
        error: (err) => console.error('Failed to create setup draft', err)
      });
    }
  }

  cancelAiSetup() {
    this.attachedFiles().forEach(f => {
      if (f.filename) {
        this.authService.deleteFile(f.filename).catch(err => console.error('Failed to delete file from storage', err));
      }
    });
    this.viewMode.set('LIST');
    this.aiSetupForm.reset({ count: 5, difficulty: 'medium' });
    this.attachedExternalContexts.set([]);
    this.attachedFiles.set([]);
    if (this.quizDraftId()) {
      const draftIdToRemove = this.quizDraftId()!;
      this.conversationService.deleteQuizDraft(draftIdToRemove).subscribe();
      this.drafts.update(drafts => drafts.filter(d => d._id !== draftIdToRemove));
      this.quizDraftId.set(null);
    }
  }

  setDifficulty(level: string) {
    this.aiSetupForm.patchValue({ difficulty: level });
  }

  async generateAiQuiz() {
    if (this.aiSetupForm.invalid) {
      this.aiSetupForm.markAllAsTouched();
      return;
    }

    const { subject, count, difficulty, context, quizType } = this.aiSetupForm.getRawValue();
    this.viewMode.set('AI_LOADING');

    // Get contentId (assuming it's passed or available via service)
    // For now we'll fetch it from ContentStateService
    const contentId = this.contentStateService.contentId;
    const fullContentText = this.contentStateService.getFullContentText();

    if (!contentId) {
      this.viewMode.set('MANUAL');
      // Show error
      return;
    }

    const externalContextIds = this.attachedExternalContexts().map((c) => c.id);
    const readyAttachments = this.attachedFiles().filter((a) => !a.uploading && a.url);
    const mediaContexts = readyAttachments.length > 0 ? readyAttachments.map((a) => ({ url: a.url!, type: a.type, filename: a.name })) : undefined;

    this.conversationService.generateQuiz(contentId, subject, count, difficulty, context, fullContentText, externalContextIds, mediaContexts, this.quizDraftId()!, quizType).subscribe({
      next: (quizContent) => {
        // Store full content
        this.fullDraftContent = quizContent;
        this.attachedExternalContexts.set([]);
        this.attachedFiles().forEach(f => {
          if (f.filename) {
            this.authService.deleteFile(f.filename).catch(err => console.error('Failed to delete file from storage', err));
          }
        });
        this.attachedFiles.set([]);

        // Determine language (default to current lang or 'en')
        const currentLang = this.translate.currentLang;
        const quizData = currentLang === 'fr' ? quizContent.fr : quizContent.en;

        if (quizContent._id) {
          this.quizDraftId.set(quizContent._id);
          this.updateLocalDraft(quizContent);
        }

        if (quizData) {
          this.quizForm.patchValue({ title: quizData.title });
          this.questions.clear();

          quizData.questions.forEach((q) => {
            q.type = q.type || (quizType === 'mix' ? 'multiple-choice' : quizType) || 'multiple-choice';
            this.questions.push(this.createQuestion(q));
          });
        }
        this.viewMode.set('MANUAL');
      },
      error: (err) => {
        console.error('Quiz generation failed', err);
        this.errorHandler.handleAiRestrictedError(err);
        this.viewMode.set('AI_SETUP'); 
      },
    });
  }

  restart() {
    if (this.quizDraftId()) {
      this.conversationService.deleteQuizDraft(this.quizDraftId()!).subscribe();
      this.quizDraftId.set(null);
    }
    this.fullDraftContent = { en: null, fr: null };
    // Reset form
    this.quizForm.reset();
    this.questions.clear();
    this.addQuestion(); // Add one empty question
    this.viewMode.set('LIST');
  }

  deleteDraft(draftId: string, trigger: MatIconButton) {
    this.confirmMenuService.confirm(trigger._elementRef, 'Global.AreYouSure', 'delete_outline').subscribe(() => {
      this.conversationService.deleteQuizDraft(draftId).subscribe(() => {
        this.loadDrafts();
        if (this.quizDraftId() === draftId) {
          this.startNewManual();
        }
      });
    });
  }

  cancel() {
    if (this.viewMode() === 'LIST') {
      this.close.emit();
    } else {
      this.viewMode.set('LIST');
    }
  }

  // --- Content Searcher Workflow ---
  private cdkOverlay = inject(Overlay);
  private searcherOverlayRef: OverlayRef | null = null;

  toggleContentSearcher() {
    if (this.searcherOverlayRef) {
      this.searcherOverlayRef.dispose();
      this.searcherOverlayRef = null;
      return;
    }

    const positionStrategy = this.cdkOverlay.position().global().centerHorizontally().centerVertically();

    this.searcherOverlayRef = this.cdkOverlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-dark-backdrop',
    });

    const portal = new ComponentPortal(ContentSearcherComponent);
    const compRef = this.searcherOverlayRef.attach(portal);

    compRef.instance.itemSelected.subscribe((result: ContentSearchResult) => this.onContentSearcherSelect(result));
    compRef.instance.closed.subscribe(() => {
      this.searcherOverlayRef?.dispose();
      this.searcherOverlayRef = null;
    });

    this.searcherOverlayRef.backdropClick().subscribe(() => {
      this.searcherOverlayRef?.dispose();
      this.searcherOverlayRef = null;
    });
  }

  async onContentSearcherSelect(result: ContentSearchResult) {
    if (!result?.id) return;
    if (this.attachedExternalContexts().find((c) => c.id === result.id)) return;

    this.attachedExternalContexts.update((contexts) => [...contexts, result]);
  }

  removeExternalContext(id: string) {
    this.attachedExternalContexts.update((contexts) => contexts.filter((c) => c.id !== id));
  }

  getExplanationLabel(question: any): string {
    const isFreeText = question.get('type')?.value === 'free-text';
    const key = isFreeText ? 'Quiz.IdealAnswerLabel' : 'Quiz.ExplanationLabel';
    const fallback = isFreeText ? 'Ideal Answer / Grading Criteria' : 'Explanation';
    const translated = this.translate.instant(key);
    return translated !== key ? translated : fallback;
  }

  getExplanationPlaceholder(question: any): string {
    const isFreeText = question.get('type')?.value === 'free-text';
    const key = isFreeText ? 'Quiz.IdealAnswerPlaceholder' : 'Quiz.ExplanationPlaceholder';
    const fallback = isFreeText ? 'Type the ideal criteria for the AI to score against...' : 'Explanation';
    const translated = this.translate.instant(key);
    return translated !== key ? translated : fallback;
  }
}
